import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ExclamationTriangleIcon,
  CreditCardIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import SubscribePanel from "../SubscribePanel";
import {
  API_BASE,
  authHeaders,
  fetchEntitlements,
  fetchSmsUsage,
  isPastDue,
  money,
  priceLabel,
  shortDate,
  statusLabel,
} from "@/lib/billing";

function toast(text) {
  Toastify({
    text,
    duration: 2600,
    style: { background: "#111827", borderRadius: "6px" },
    className: "shadow-xl border-none",
    position: "right",
    stopOnFocus: false,
  }).showToast();
}

/** Usage meter. `limit: null` means unlimited — render no bar. */
function Meter({ label, meter, unit }) {
  if (!meter) return null;
  const { used, limit, unlimited, overLimit, atLimit, scope } = meter;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(limit, 1)) * 100);
  const barColor = overLimit || atLimit ? "#dc2626" : "#5e6ad2";

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] font-medium text-[#0f0f14]">{label}</span>
        <span className="text-[12px] tabular-nums text-[#6b6b76]">
          {unlimited
            ? `${used.toLocaleString()} · unlimited`
            : `${used.toLocaleString()} / ${limit.toLocaleString()}`}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#f4f4f5]">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
      )}
      {/* Over-limit is surfaced, never hidden. Existing records stay usable —
          only creating new ones is refused. */}
      {overLimit && (
        <p className="mt-1.5 text-[12px] leading-snug text-[#b91c1c]">
          Over the plan limit. Your existing {unit} are all still here and
          editable — you just can&apos;t create new ones until you upgrade.
        </p>
      )}
      {!overLimit && atLimit && (
        <p className="mt-1.5 text-[12px] text-[#b91c1c]">
          Limit reached — upgrade to add more {unit}.
        </p>
      )}
      {scope === "lifetime" && !unlimited && (
        <p className="mt-1 text-[11px] text-[#a0a0ab]">
          Lifetime total — deleting an invoice doesn&apos;t free up quota.
        </p>
      )}
    </div>
  );
}

export default function PlanCard() {
  const [ent, setEnt] = useState(null);
  const [sms, setSms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPlans, setOpenPlans] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const e = await fetchEntitlements();
      setEnt(e);
      if (e && e.features && e.features.sms) {
        try {
          setSms(await fetchSmsUsage());
        } catch {
          setSms(null);
        }
      } else {
        setSms(null);
      }
    } catch {
      toast("Could not load your plan.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}${path}`, {}, authHeaders());
      toast((data && data.message) || okMsg);
      await load();
    } catch (err) {
      toast(
        (err && err.response && err.response.data && err.response.data.message) ||
          "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="h-64 animate-pulse rounded-lg border border-[#e4e4e7] bg-[#fafafa]" />
    );
  }
  if (!ent) return null;

  const sub = ent.subscription;
  const plan = ent.plan;
  const pastDue = isPastDue(sub);
  const dunning = sub && sub.dunning;
  const card = sub && sub.paymentCardDetails;
  const needsCard = ent.smsAllowed && ent.smsAllowed.reason === "no_card_on_file";

  return (
    <div className="space-y-4">
      {/* ---------- dunning banner ---------- */}
      {pastDue && (
        <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] p-4">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-[#991b1b]">
                We couldn&apos;t charge your card
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-[#7f1d1d]">
                Your account keeps working normally — invoices, customers and
                payment collection are unaffected. Only SMS is paused until this
                clears.
                {dunning && dunning.nextAttemptAt
                  ? ` We'll try again on ${shortDate(dunning.nextAttemptAt)}.`
                  : " We won't retry automatically again."}
              </p>
              {dunning && dunning.hostedInvoiceUrl && (
                <a
                  href={dunning.hostedInvoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-block rounded-md bg-[#dc2626] px-3.5 py-2 text-[13px] font-medium text-white hover:bg-[#b91c1c]"
                >
                  Pay or update card
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------- plan ---------- */}
      <div className="rounded-lg border border-[#e4e4e7] bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[12px] uppercase tracking-wide text-[#a0a0ab]">
              Current plan
            </p>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <h3 className="text-[20px] font-semibold text-[#0f0f14]">
                {plan.label}
              </h3>
              <span className="text-[13px] text-[#6b6b76]">
                {plan.priceMonthly > 0 ? priceLabel(plan) : "no charge"}
              </span>
              {sub && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    pastDue
                      ? "bg-[#fee2e2] text-[#b91c1c]"
                      : "bg-[#5e6ad2]/10 text-[#5e6ad2]"
                  }`}
                >
                  {statusLabel(sub)}
                </span>
              )}
            </div>

            {/* pending scheduled downgrade */}
            {sub && sub.pendingPlan && (
              <p className="mt-2 flex items-center gap-1.5 text-[13px] text-[#6b6b76]">
                <ArrowDownIcon className="h-3.5 w-3.5 text-[#a0a0ab]" />
                Downgrading to{" "}
                <strong className="font-medium text-[#0f0f14]">
                  {(ent.plans.find((p) => p.key === sub.pendingPlan) || {}).label ||
                    sub.pendingPlan}
                </strong>{" "}
                on {shortDate(sub.pendingPlanEffectiveAt) || "your renewal date"}
              </p>
            )}

            {sub && sub.cancelAtPeriodEnd && !sub.pendingPlan && (
              <p className="mt-2 text-[13px] text-[#6b6b76]">
                Cancels on {shortDate(sub.currentPeriodEnd) || "period end"} — you
                keep access until then.
              </p>
            )}

            {sub && !sub.cancelAtPeriodEnd && sub.currentPeriodEnd && !pastDue && (
              <p className="mt-2 text-[13px] text-[#6b6b76]">
                Renews {shortDate(sub.currentPeriodEnd)}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setOpenPlans(true)}
              className="rounded-md bg-[#5e6ad2] px-3.5 py-2 text-[13px] font-medium text-white hover:bg-[#4e5acb]"
            >
              {ent.upgradeTo ? "Change plan" : "View plans"}
            </button>
            {sub && sub.cancelAtPeriodEnd && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  act("/company/subscription/resume", "Subscription resumed.")
                }
                className="rounded-md border border-[#e4e4e7] bg-white px-3.5 py-2 text-[13px] font-medium text-[#0f0f14] hover:border-[#d4d4d8] disabled:opacity-50"
              >
                Keep my plan
              </button>
            )}
          </div>
        </div>

        {/* card on file */}
        <div className="mt-4 flex items-center gap-2 border-t border-[#e4e4e7] pt-4 text-[13px] text-[#6b6b76]">
          <CreditCardIcon className="h-4 w-4 text-[#a0a0ab]" />
          {card && card.last4 ? (
            <span>
              {card.brand ? card.brand.toUpperCase() : "Card"} ending {card.last4}
            </span>
          ) : (
            <span>No card on file</span>
          )}
        </div>
      </div>

      {/* ---------- usage ---------- */}
      <div className="rounded-lg border border-[#e4e4e7] bg-white p-5">
        <h4 className="text-[15px] font-semibold text-[#0f0f14]">Usage</h4>
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          <Meter label="Invoices" meter={ent.usage.invoices} unit="invoices" />
          <Meter label="Customers" meter={ent.usage.customers} unit="customers" />
          <Meter label="Users" meter={ent.usage.users} unit="users" />
        </div>
      </div>

      {/* ---------- SMS ---------- */}
      {ent.features.sms && (
        <div className="rounded-lg border border-[#e4e4e7] bg-white p-5">
          <div className="flex items-baseline justify-between">
            <h4 className="text-[15px] font-semibold text-[#0f0f14]">
              SMS this period
            </h4>
            {sms && sms.usage && (
              <span className="text-[12px] tabular-nums text-[#6b6b76]">
                {sms.usage.segments.toLocaleString()} /{" "}
                {sms.usage.included.toLocaleString()} included
              </span>
            )}
          </div>

          {/* card required — the gate returns no_card_on_file */}
          {needsCard && (
            <div className="mt-3 rounded-md border border-[#fde68a] bg-[#fffbeb] p-3">
              <p className="text-[13px] leading-relaxed text-[#92400e]">
                <strong className="font-semibold">Add a card to enable SMS.</strong>{" "}
                SMS overage is billed per segment, so we need a card on file
                before sending.
              </p>
            </div>
          )}

          {!needsCard && ent.smsAllowed && !ent.smsAllowed.allowed && (
            <div className="mt-3 rounded-md border border-[#e4e4e7] bg-[#fafafa] p-3">
              <p className="text-[13px] text-[#6b6b76]">
                {ent.smsAllowed.message || "SMS is currently unavailable."}
              </p>
            </div>
          )}

          {sms && sms.usage && (
            <>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#f4f4f5]">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      100,
                      (sms.usage.segments / Math.max(sms.usage.included, 1)) * 100,
                    )}%`,
                    backgroundColor:
                      sms.usage.overageSegments > 0 ? "#d97706" : "#5e6ad2",
                  }}
                />
              </div>
              <dl className="mt-4 grid grid-cols-3 gap-4 text-[13px]">
                <div>
                  <dt className="text-[#6b6b76]">Messages</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-[#0f0f14]">
                    {sms.usage.messages.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#6b6b76]">Overage segments</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-[#0f0f14]">
                    {sms.usage.overageSegments.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#6b6b76]">Est. overage</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-[#0f0f14]">
                    {money(sms.usage.estimatedOverageUsd)}
                  </dd>
                </div>
              </dl>
              {sms.billable === false && (
                <p className="mt-3 text-[12px] leading-snug text-[#b45309]">
                  Metered billing isn&apos;t attached to this subscription yet, so
                  SMS usage is being recorded but not invoiced.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <SubscribePanel open={openPlans} setOpen={setOpenPlans} onChanged={load} />
    </div>
  );
}
